'use client';

import React from 'react';
import { X } from 'lucide-react';
import Button from '@/components/ui/Button';
import Drawer from '@/components/ui/Drawer';
import Container from '@/components/layout/Container';
import Flex from '@/components/layout/Flex';
import Section from '@/components/layout/Section';
import Spacer from '@/components/shared/Spacer';
import {
  TypographyH2,
  TypographyH4,
  TypographySmall,
} from '@/components/shared/Typography';
import {
  EMAIL,
  HREF_EMAIL,
  HREF_PHONE_NUMBER,
  PHONE_NUMBER,
} from '@/lib/consts';
import { cn } from '@/lib/utils';
import { useViewport } from '@/components/providers/ViewportProvider';
import BackgroundImage from '@/components/media/BackgroundImage';
import Column from '@/components/layout/Column';
import Visibility from '@/components/shared/Visibility';
import LogoButton from '@/components/project/LogoButton';
import { MENU_CTAS, NAVIGATION_LIST } from './consts';

// TODO: Move into @/components/project
function Menu() {
  const { isMobileMode } = useViewport();

  return (
    <Drawer.Content className="bg-soft-charcoal-gray">
      <Section.Base className="overflow-auto overscroll-contain w-screen max-h-full touch-auto">
        <Container maxWidth="max-w-5xl" className="relative h-full">
          <Drawer.Close className="absolute top-4 right-4" asChild={true}>
            <Button
              size="icon"
              variant="outline"
              className="p-2 text-white bg-transparent border-white"
            >
              <X />
            </Button>
          </Drawer.Close>

          <Flex fill={true} direction="column" responsive={{ md: 'md:flex-row' }}>
            <Column fill={true} justify="center" items="center" className="flex-1">
              <Drawer.Header>
                <Drawer.Title className="text-white" />

                <Drawer.Close asChild={true}>
                  <LogoButton presetRatio="square" />
                </Drawer.Close>
              </Drawer.Header>

              {/* Navigation */}
              <Flex fill={true} direction="column" justify="start">
                {NAVIGATION_LIST.map(({ label, href, variant }, index) => (
                  <Drawer.Close key={label} asChild={true}>
                    <Button.Link
                      variant={variant}
                      href={href ?? '/'}
                      className="gap-2 justify-start w-full"
                    >
                      <span className="text-gold">0{index + 1}</span>
                      <TypographyH2 className="w-full text-white">
                        {label}
                      </TypographyH2>
                    </Button.Link>
                  </Drawer.Close>
                ))}
              </Flex>
            </Column>

            <Spacer size="h-8" />

            <BackgroundImage
              src="placeholder_lng6fq"
              alt="Placeholder Image"
              fill={true}
              sizes="100vw"
              presetRatio="tall"
              overlay={cn(!isMobileMode && 'bg-black/50 dackdrop-blur-sm')}
              imageClassName="object-cover"
              className="flex-1 bg-transparent"
              showImage={!isMobileMode}
            >
              <Column fill={true} justify="around" items="center">
                <Flex direction="column" gap="gap-2">
                  <TypographyH4 className="text-gold">CONTACT</TypographyH4>

                  <TypographySmall className="text-white">
                    <span className="font-bold">PHONE:</span>
                    <Button.Link
                      variant="link"
                      className="font-normal text-white underline"
                      href={HREF_PHONE_NUMBER}
                    >
                      {PHONE_NUMBER}
                    </Button.Link>
                  </TypographySmall>

                  <TypographySmall className="text-white">
                    <span className="font-bold">EMAIL:</span>
                    <Button.Link
                      variant="link"
                      className="font-normal text-white underline"
                      href={HREF_EMAIL}
                    >
                      {EMAIL}
                    </Button.Link>
                  </TypographySmall>

                  <TypographySmall className="text-white">
                    <span className="font-bold">HOURS:</span> 8-4P MONDAY -
                    FRIDAY
                  </TypographySmall>
                </Flex>

                <Visibility shouldShow={isMobileMode}>
                  <Spacer size="h-4" />
                </Visibility>

                {/* CTAs */}
                <Flex direction="column" gap="gap-4">
                  {MENU_CTAS.map(cta => (
                    <Drawer.Close key={cta.label} asChild={true}>
                      <Button.Icon {...cta} />
                    </Drawer.Close>
                  ))}
                </Flex>
              </Column>
            </BackgroundImage>
          </Flex>
        </Container>
      </Section.Base>
    </Drawer.Content>
  );
}

export default React.memo(Menu);
